import type { HsGraphics } from '@lib/core-ts/graphic-render-new/graphics.class';
import type { HsMaterial } from '@lib/core-ts/graphic-render-new/materials/material.class';
import type { HsGraphicCollection } from '@lib/core-ts/graphic-render-new/objects/graphic-collection.class';
import type { HsGraphicObject } from '@lib/core-ts/graphic-render-new/objects/graphic-object.class';
import { NodeTint } from '../../showcase/host/node-tint.class';

/** Color of highlighted instances of instanced meshes; single meshes take the highlight material. */
const HIGHLIGHT_RGBA: readonly [number, number, number, number] = [1, 0.82, 0.18, 1];

/** What the highlight reads of the drawn scene. */
export interface ViewerSelectionHighlightScene {
  /** Drawn meshes by render key; instanced keys map to their collection. */
  meshes(): ReadonlyMap<string, HsGraphicObject>;
  /** Render keys of a collection's instances, in instance order. */
  collectionRenderKeys(collection: HsGraphicCollection): readonly string[] | undefined;
  nodeIdOf(renderKey: string): string | null;
  graphics(): HsGraphics | null;
  /** The highlight material for single meshes, built with the scene's look so clipping and lighting match. */
  material(): HsMaterial | null;
}

/**
 * Shows the selected scene nodes in the 3D view: single meshes swap to a highlight material and instances of
 * instanced meshes take a highlight color. Scene refreshes replace objects and buffers, so the page applies the
 * highlight again after each one. One tint group of the showcase's `NodeTint` does the work; this class only
 * supplies the stand's highlight material, built once per highlight.
 */
export class ViewerSelectionHighlight {
  private readonly tint: NodeTint;
  private material: HsMaterial | null = null;

  public constructor(private readonly scene: ViewerSelectionHighlightScene) {
    this.tint = new NodeTint({
      meshes: () => scene.meshes(),
      collectionRenderKeys: (collection: HsGraphicCollection) => scene.collectionRenderKeys(collection),
      nodeIdOf: (renderKey: string) => scene.nodeIdOf(renderKey),
      graphics: () => scene.graphics(),
      material: () => this.material,
    });
  }

  /** Highlights every drawn mesh of `nodeIds`, replacing the previous highlight; returns the highlighted count. */
  public apply(nodeIds: ReadonlySet<string>): number {
    this.clear();
    this.material = this.scene.material();
    if (nodeIds.size === 0 || this.material === null) {
      return 0;
    }
    return this.tint.apply([{ nodeIds, rgba: HIGHLIGHT_RGBA }], null);
  }

  /** Restores the materials and instance colors the highlight replaced. */
  public clear(): void {
    this.tint.clear();
  }
}
